import type { MediaAccessErrorSubType } from '@openphone/desktop-client'
import type { ReactNode } from 'react'

interface UserMediaDeviceErrorPromptContext {
  mediaDevice: { capitalized: string; lowercase: string }
  originalError: Error
}

interface UserMediaDeviceErrorPrompt {
  title: string
  message: ReactNode
}

type CreateUserMediaDeviceErrorPrompt = (
  ctx: UserMediaDeviceErrorPromptContext,
) => UserMediaDeviceErrorPrompt

type UserMediaDeviceErrorMap = Readonly<
  Record<MediaAccessErrorSubType, CreateUserMediaDeviceErrorPrompt>
>

const unknownErrorPrompt: CreateUserMediaDeviceErrorPrompt = ({ mediaDevice }) => ({
  title: `${mediaDevice.capitalized} Unknown`,
  message: `An unknown error occurred while requesting access to your ${mediaDevice.lowercase}. Please try again. If the problem persists, please contact support. We have been notified of this issue.`,
})

const deniedErrorPrompt: CreateUserMediaDeviceErrorPrompt = ({ mediaDevice }) => ({
  title: `${mediaDevice.capitalized} access was denied`,
  message: `You have denied ${mediaDevice.lowercase} access, update or reset your permissions in your browser or device settings.`,
})

const userMediaDeviceErrorMap: UserMediaDeviceErrorMap = {
  /**
   * Treat denied as an error because anything other than "granted" or "prompt" or "not-determined" is a failure case.
   */
  denied: deniedErrorPrompt,

  /**
   * Thrown if one or more of the requested source devices cannot be used at this time.
   * This will happen if the browsing context is insecure (that is, the page was loaded using HTTP rather than HTTPS).
   * It also happens if the user has specified that the current browsing instance is not permitted access to the device,
   * the user has denied access for the current session, or the user has denied all access to user media devices globally.
   * On browsers that support managing media permissions with Permissions Policy, this error is returned if
   * Permissions Policy is not configured to allow access to the input source(s).
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
   */
  'not-allowed': deniedErrorPrompt,

  /**
   * Although the user and operating system both granted access to the hardware device, and
   * no hardware issues occurred that would cause a NotReadableError DOMException, throw if
   * some problem occurred which prevented the device from being used.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  aborted: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} access was aborted`,
    message: `OpenPhone was not allowed to access your ${mediaDevice.lowercase} because the tab was not in focus. Please ensure that the tab is in focus and try again. If the problem persists, please restart your browser.`,
  }),

  /**
   * Thrown if current document is not fully active.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  'invalid-state': ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} Access Not Allowed`,
    message: `OpenPhone was not allowed to access your ${mediaDevice.lowercase} because the tab was not in focus. Please ensure that the tab is in focus and try again. If the problem persists, please restart your browser.`,
  }),

  /**
   * Thrown if the list of constraints specified is empty, or has all constraints set to false.
   * This can also happen if you try to call getUserMedia() in an insecure context, since
   * navigator.mediaDevices is undefined in an insecure context.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  'type-error': ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} Type Error`,
    message: `OpenPhone was not able to find your ${mediaDevice.lowercase}. Please ensure that a ${mediaDevice.lowercase} is connected and try again.`,
  }),

  /**
   * Thrown if the specified constraints resulted in no candidate devices which met the criteria requested.
   * The error is an object of type OverconstrainedError, and has a constraint property whose string value is
   * the name of a constraint which was impossible to meet, and a message property containing a human-readable string
   * explaining the problem.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  overconstrained: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} Overconstrained`,
    message: `OpenPhone was not able to find your ${mediaDevice.lowercase}. Please ensure that a ${mediaDevice.lowercase} is connected and try again.`,
  }),

  /**
   * A Restricted error means even if the user has granted access, the operating system
   * has restricted access to the microphone. this could be for a variety of reasons
   * such as environment policies (MDM, parental controls, etc.)
   */
  restricted: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} Restricted`,
    message: `OpenPhone was not able to find your ${mediaDevice.lowercase}. Please ensure that a ${mediaDevice.lowercase} is connected and try again.`,
  }),

  /**
   * Thrown if user media support is disabled on the Document on which getUserMedia() was called.
   * The mechanism by which user media support is enabled and disabled is left up to the individual user agent.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  security: ({ mediaDevice }) => ({
    title: `${mediaDevice.capitalized} Security`,
    message: `OpenPhone was not able to find your ${mediaDevice.lowercase}. Please ensure that a ${mediaDevice.lowercase} is connected and try again.`,
  }),

  /**
   * Thrown if no media tracks of the type specified were found that satisfy the given constraints.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
   */
  'not-found': unknownErrorPrompt,

  /**
   * Thrown if, although the user granted permission to use the matching devices, a hardware error occurred at the operating system, browser, or Web page level which prevented access to the device.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
   */
  'not-readable': unknownErrorPrompt,

  /**
   * Thrown if, although the user granted permission to use the matching devices, a hardware error occurred at the operating system, browser, or Web page level which prevented access to the device.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
   */
  unknown: unknownErrorPrompt,
}

export function getUserMediaDeviceErrorPrompt(
  subType: MediaAccessErrorSubType,
  ctx: UserMediaDeviceErrorPromptContext,
): UserMediaDeviceErrorPrompt {
  return userMediaDeviceErrorMap[subType](ctx)
}
